import type { ScreenType, StandardState } from '@/modules/gem-meter/types/types';

import _cloneDeep from 'lodash.clonedeep';

import GemMeterBaseRule from '../GemMeterBaseRule';
import {
  commitStandardList,
  getElementGroup,
  getElementOfRule,
  getElementsOfRules,
  getPageMetric,
  getStandardByRuleId,
} from '../../Engine/useGemMeterStore';
import { DIFFERENT_BUTTON_FONT_FAMILY, SHOPABLE_PREFIX } from '../../../constants/rulesConstants';
import GemMeterHelper from '../../Engine/v2/GemMeterHelper';

// specialKey
// ADD-elementMerId
// REMOVE-elementMerId
// EDIT-elementMerId
// SHOW-elementMerId
// HIDE-elementMerId
export default class DifferentButtonFontFamily extends GemMeterBaseRule {
  static ruleId = DIFFERENT_BUTTON_FONT_FAMILY.id;

  static validate(specialKey: string, screenType: ScreenType) {
    const ruleId = DifferentButtonFontFamily.ruleId;
    const standards = getStandardByRuleId(ruleId);
    const action = specialKey.split('@').shift();
    const elementMerId = specialKey.split('@').pop() as string;

    if (action === 'ADD') {
      DifferentButtonFontFamily.onAddElement(standards, screenType, elementMerId);
      DifferentButtonFontFamily.runAddOnOtherScreen(standards, screenType, elementMerId);
    } else if (action === 'REMOVE') {
      DifferentButtonFontFamily.onRemoveElement(standards, screenType, elementMerId);
      DifferentButtonFontFamily.runRemoveOnOtherScreen(standards, screenType, elementMerId);
    } else if (action === 'EDIT') {
      DifferentButtonFontFamily.onEditElement(standards, screenType, elementMerId);
    } else if (action === 'SHOW') {
      DifferentButtonFontFamily.onAddElement(standards, screenType, elementMerId);
    } else if (action === 'HIDE') {
      DifferentButtonFontFamily.onRemoveElement(standards, screenType, elementMerId);
    }

    return true;
  }

  static isValid(screenType: ScreenType) {
    const elementGroups = getElementGroup();
    const elementGroup = elementGroups[screenType][DIFFERENT_BUTTON_FONT_FAMILY.id] || {};
    const elementOfRules = getElementsOfRules();
    let previousFontFamily = null;
    let isValid = true;
    for (const elementMerId of Object.keys(elementGroup)) {
      const domElement = GemMeterBaseRule.getElement(`${elementMerId} .button`);
      if (domElement && elementOfRules[elementMerId][screenType].visible) {
        const fontFamily = getComputedStyle(domElement).fontFamily;
        if (previousFontFamily !== null && previousFontFamily !== fontFamily) {
          isValid = false;
          break;
        }

        previousFontFamily = fontFamily;
      }
    }

    return isValid;
  }

  static onAddElement(standards: StandardState, screenType: ScreenType, elementMerId: string) {
    const elementOfRule = getElementOfRule(elementMerId);
    if (!elementOfRule || !elementOfRule[screenType].visible) {
      return;
    }

    const metric = getPageMetric();
    const ruleId = DifferentButtonFontFamily.ruleId;
    standards[ruleId].screen[screenType].elementLength += 1;
    const currentStatus = standards[ruleId].screen[screenType].status;
    if (currentStatus === 'fixed') {
      const isValid = DifferentButtonFontFamily.isValid(screenType);
      if (isValid) {
        metric[screenType].fixed += 1;
        standards[ruleId].screen[screenType].status = 'fixed';
      } else {
        const elementGroups = getElementGroup();
        const elementGroup = elementGroups[screenType][DIFFERENT_BUTTON_FONT_FAMILY.id] || {};
        metric[screenType].fixed -= Object.keys(elementGroup).length - 1;
        metric[screenType].errors += Object.keys(elementGroup).length;
        standards[ruleId].screen[screenType].status = 'error';
        for (const elementMerIdIn of Object.keys(elementGroup)) {
          const gemMeterElement = GemMeterHelper.createGemMeterElement('button', elementMerIdIn);
          standards[ruleId].screen[screenType].errorElements[elementMerIdIn] = gemMeterElement;
        }
      }
    } else if (currentStatus === 'error') {
      metric[screenType].errors += 1;
      const gemMeterElement = GemMeterHelper.createGemMeterElement('button', elementMerId);
      standards[ruleId].screen[screenType].errorElements[elementMerId] = gemMeterElement;
    } else if (currentStatus === 'skipped') {
      metric[screenType].skipped += 1;
      const gemMeterElement = GemMeterHelper.createGemMeterElement('button', elementMerId);
      gemMeterElement.status = 'skip';
      gemMeterElement.visible = false;
      standards[ruleId].screen[screenType].errorElements[elementMerId] = gemMeterElement;
    } else {
      const isValid = DifferentButtonFontFamily.isValid(screenType);
      if (isValid) {
        metric[screenType].fixed += 1;
        standards[ruleId].screen[screenType].status = 'fixed';
      } else {
        metric[screenType].errors += 1;
        standards[ruleId].screen[screenType].status = 'error';
      }
    }

    commitStandardList(ruleId.includes(SHOPABLE_PREFIX) ? 'shopableStandards' : 'convertableStandards', standards);
  }

  static runAddOnOtherScreen(standards: StandardState, screenType: ScreenType, elementMerId: string) {
    const metric = getPageMetric();
    const ruleId = DifferentButtonFontFamily.ruleId;
    (['lg', 'md', 'sm', 'xs'] as ScreenType[]).forEach((screenT) => {
      if (screenT !== screenType) {
        standards[ruleId].screen[screenT].elementLength += 1;
        const gemMeterElement = GemMeterHelper.createGemMeterElement('button', elementMerId);
        switch (standards[ruleId].screen[screenT].status) {
          case 'error': {
            metric[screenT].errors += 1;
            standards[ruleId].screen[screenT].errorElements[elementMerId] = gemMeterElement;
            break;
          }
          case 'skipped': {
            metric[screenT].skipped += 1;
            gemMeterElement.status = 'skip';
            gemMeterElement.visible = false;
            standards[ruleId].screen[screenT].errorElements[elementMerId] = gemMeterElement;
            break;
          }
          default:
            metric[screenT].errors += 1;
            standards[ruleId].screen[screenT].errorElements[elementMerId] = gemMeterElement;
            break;
        }
      }
    });

    commitStandardList(ruleId.includes(SHOPABLE_PREFIX) ? 'shopableStandards' : 'convertableStandards', standards);
  }

  static onRemoveElement(standards: StandardState, screenType: ScreenType, elementMerId: string) {
    const elementOfRule = getElementOfRule(elementMerId);
    if (!elementOfRule || !elementOfRule[screenType].visible) {
      return;
    }

    const metric = getPageMetric();
    const ruleId = DifferentButtonFontFamily.ruleId;
    standards[ruleId].screen[screenType].elementLength -= 1;
    const currentStatus = standards[ruleId].screen[screenType].status;
    if (currentStatus === 'skipped') {
      metric[screenType].skipped -= 1;
      delete standards[ruleId].screen[screenType].errorElements[elementMerId];
      if (standards[ruleId].screen[screenType].elementLength === 0) {
        standards[ruleId].screen[screenType].status = null;
      }
    } else if (currentStatus === 'error') {
      metric[screenType].errors -= 1;
      delete standards[ruleId].screen[screenType].errorElements[elementMerId];
      if (standards[ruleId].screen[screenType].elementLength > 0) {
        const isValid = DifferentButtonFontFamily.isValid(screenType);
        if (isValid) {
          metric[screenType].fixed += standards[ruleId].screen[screenType].elementLength;
          standards[ruleId].screen[screenType].status = 'fixed';
        }
      } else {
        standards[ruleId].screen[screenType].status = null;
      }
    } else {
      metric[screenType].fixed -= 1;
      if (standards[ruleId].screen[screenType].elementLength === 0) {
        standards[ruleId].screen[screenType].status = null;
      }
    }

    commitStandardList(ruleId.includes(SHOPABLE_PREFIX) ? 'shopableStandards' : 'convertableStandards', standards);
  }

  static runRemoveOnOtherScreen(standards: StandardState, screenType: ScreenType, elementMerId: string) {
    const metric = getPageMetric();
    const ruleId = DifferentButtonFontFamily.ruleId;
    (['lg', 'md', 'sm', 'xs'] as ScreenType[]).forEach((screenT) => {
      if (screenT !== screenType) {
        standards[ruleId].screen[screenT].elementLength -= 1;
        switch (standards[ruleId].screen[screenT].status) {
          case 'skipped': {
            metric[screenT].skipped -= 1;
            delete standards[ruleId].screen[screenT].errorElements[elementMerId];
            break;
          }
          case 'fixed': {
            metric[screenT].fixed -= 1;
          }
          default: {
            metric[screenT].errors -= 1;
            delete standards[ruleId].screen[screenT].errorElements[elementMerId];
            break;
          }
        }

        if (standards[ruleId].screen[screenT].elementLength === 0) {
          standards[ruleId].screen[screenT].status = null;
        }
      }
    });

    commitStandardList(ruleId.includes(SHOPABLE_PREFIX) ? 'shopableStandards' : 'convertableStandards', standards);
  }

  static onEditElement(standards: StandardState, screenType: ScreenType, elementMerId: string) {
    const elementOfRule = getElementOfRule(elementMerId);
    if (!elementOfRule || !elementOfRule[screenType].visible) {
      return;
    }

    const elementGroups = getElementGroup();
    const elementGroup = elementGroups[screenType][DIFFERENT_BUTTON_FONT_FAMILY.id] || {};
    const ruleId = DifferentButtonFontFamily.ruleId;
    const currentStatus = standards[ruleId].screen[screenType].status;
    if (currentStatus === 'skipped') {
      standards[ruleId].screen[screenType].elementLength = Object.keys(elementGroup).length;

      return;
    }

    const metric = getPageMetric();
    const isValid = DifferentButtonFontFamily.isValid(screenType);
    if (!isValid && currentStatus === 'fixed') {
      standards[ruleId].screen[screenType].status = 'error';
      metric[screenType].fixed -= standards[ruleId].screen[screenType].elementLength;
      standards[ruleId].screen[screenType].elementLength = Object.keys(elementGroup).length;
      metric[screenType].errors += standards[ruleId].screen[screenType].elementLength;
      Object.values(elementGroup).forEach((element) => {
        const gemElement = GemMeterHelper.createGemMeterElement(element.key, element.merId);
        standards[ruleId].screen[screenType].errorElements[element.merId] = gemElement;
      });
    }

    if (!isValid && currentStatus === null) {
      metric[screenType].fixed -= standards[ruleId].screen[screenType].elementLength;
      standards[ruleId].screen[screenType].elementLength = Object.keys(elementGroup).length;
      metric[screenType].errors += standards[ruleId].screen[screenType].elementLength;
      standards[ruleId].screen[screenType].status = 'error';
      Object.values(elementGroup).forEach((element) => {
        const gemElement = GemMeterHelper.createGemMeterElement(element.key, element.merId);
        standards[ruleId].screen[screenType].errorElements[element.merId] = gemElement;
      });
    }

    if (isValid && currentStatus === 'error') {
      metric[screenType].errors -= standards[ruleId].screen[screenType].elementLength;
      standards[ruleId].screen[screenType].elementLength = Object.keys(elementGroup).length;
      metric[screenType].fixed += standards[ruleId].screen[screenType].elementLength;
      standards[ruleId].screen[screenType].status = 'fixed';
      standards[ruleId].screen[screenType].errorElements = {};
    }

    if (isValid && currentStatus === null) {
      metric[screenType].errors -= standards[ruleId].screen[screenType].elementLength;
      standards[ruleId].screen[screenType].elementLength = Object.keys(elementGroup).length;
      metric[screenType].fixed += standards[ruleId].screen[screenType].elementLength;
      standards[ruleId].screen[screenType].elementLength = Object.keys(elementGroup).length;
      standards[ruleId].screen[screenType].status = 'fixed';
      standards[ruleId].screen[screenType].errorElements = {};
    }
  }
}
